import React, { useState, useEffect } from 'react';
import OlcButton from '~/components/General/OlcButton';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

// Hooks
import useScreenWidth from '~/hooks/useScreenWidth';

// Utils
import { dateFormat, formatCallDuration } from '~/utils/date-format';
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION } from '~/utils/constants';
import { formatPhoneNumber } from '~/utils/helperFunctions';

// MUI Components
import {
  Box,
  debounce,
  IconButton,
  Input,
  InputAdornment,
  Tooltip,
  Typography,
  GlobalStyles,
} from '@mui/material';
import DataGrid from '~/components/General/OlcDataGrid';
import OlcTablePagination from '~/components/General/OlcTablePagination';

// Icons
import AddIcon from '@mui/icons-material/Add';
import {
  ContactSearchCIcon,
  HistoryCIcon,
  EditPhoneCIcon,
  ReleasePhoneCIcon,
  ReleasePhoneNumberCIcon,
  CustomFieldIcon,
} from '~/assets/images';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';

// Redux Actions
import {
  fetchPhoneTrackingNumbers,
  fetchPhoneTrackingHistory,
  fetchSmsTrackingHistory,
  releasePhoneTrackingNumber,
} from '~/redux/actions/phone-tracking-actions';
import { getAllPaymentMethods } from '~/redux/actions/stripe-actions';
import { failure } from '~/redux/actions/snackbar-actions';

// Components
import ActivateTrackingModal from '~/components/PhoneTracking/ActivateTrackingModal';
import LinkedTemplatesModal from '~/components/PhoneTracking/LinkedTemplatesModal';
import MultiSelect from '~/components/Contacts/AdvanceFilters/MultiSelect';
import ConfirmModal from '~/components/General/ConfirmModal';
import NoTrackingScreen from '~/components/PhoneTracking/NoTrackingScreen';
import NoRecords from '~/components/General/NoRecords';
import PaymentMethod from '~/components/Account/Wallet/PaymentMethod';
import Stripe from '~/components/Stripe/Stripe';
import OlcPageBackButton from '~/components/General/OlcPageBackButton';
import OlcActionMenu from '~/components/General/OlcActionMenu';

// Styles
import './styles.scss';

const classes = {
  row: 'phoneTrackingDataGridRow',
  cell: 'phoneTrackingDataGridCell',
  cellContent: 'phoneTrackingCellContent',
  virtualScroller: 'phoneTrackingVirtualScroller',
};

const PhoneTracking = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: trackingId, type: historyType } = useParams();
  const isWideScreen = useScreenWidth(1600);
  const isMediumScreen = useScreenWidth(1400);
  const isCompanyEnabled = false;
  const pageSizes = DEFAULT_PAGE_SIZES;

  // Redux State
  const phoneTrackingNumbers = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingNumbers
  );
  const totalCount = useSelector((state) => state.phoneTrackingReducers.rowsCount);
  const totalRecordsInDB = useSelector((state) => state.phoneTrackingReducers.totalRecordsInDB);
  const loading = useSelector((state) => state.phoneTrackingReducers.loading);
  const perPage = useSelector(
    (state) => state.phoneTrackingReducers.perPage || DEFAULT_PAGINATION.pageSize
  );
  const lastPage = useSelector((state) => state.phoneTrackingReducers.lastPage);
  const from = useSelector((state) => state.phoneTrackingReducers.from);
  const to = useSelector((state) => state.phoneTrackingReducers.to);
  const currentPage = useSelector((state) => state.phoneTrackingReducers.currentPage);
  const phoneTrackingHistory = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingHistory
  );
  const historyCount = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingHistoryCount
  );
  const historyFrom = useSelector((state) => state.phoneTrackingReducers.phoneTrackingHistoryFrom);
  const historyTo = useSelector((state) => state.phoneTrackingReducers.phoneTrackingHistoryTo);
  const historyLastPage = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingHistoryLastPage
  );
  const historyCurrentPage = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingHistoryCurrentPage
  );
  const historyPerPage = useSelector(
    (state) =>
      state.phoneTrackingReducers.phoneTrackingHistoryPerPage || DEFAULT_PAGINATION.pageSize
  );
  const historyLoader = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingHistoryLoader
  );
  const phoneTrackingSmsHistory = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingSmsHistory
  );
  const smsHistoryCount = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingSmsHistoryCount
  );
  const smsHistoryFrom = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingSmsHistoryFrom
  );
  const smsHistoryTo = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingSmsHistoryTo
  );
  const smsHistoryLastPage = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingSmsHistoryLastPage
  );
  const smsHistoryCurrentPage = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingSmsHistoryCurrentPage
  );
  const smsHistoryPerPage = useSelector(
    (state) =>
      state.phoneTrackingReducers.phoneTrackingSmsHistoryPerPage || DEFAULT_PAGINATION.pageSize
  );
  const smsHistoryLoader = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingSmsHistoryLoader
  );
  const paymentMethods = useSelector((state) => state.stripeReducers?.methods || []);
  const isTestMode = useSelector((state) => state.userReducers.user.isLiveMode === false);

  // Local State
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const showHistory = Boolean(trackingId);
  const historyRequestKey = showHistory ? `${trackingId}:${historyType || 'call'}` : null;
  const [loadedHistoryRequestKey, setLoadedHistoryRequestKey] = useState(null);
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState(false);
  const [modalOpen, setModalOpen] = useState({
    type: '',
    open: false,
  });
  const [linkedTemplatesModal, setLinkedTemplatesModal] = useState({
    open: false,
    templates: [],
  });
  const [paymentMethodModal, setPaymentMethodModal] = useState(false);
  const [wasPaymentMethodModalOpen, setWasPaymentMethodModalOpen] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState({
    type: '',
    open: false,
    message: '',
    handleOk: () => {},
    description: '',
    loading: false,
    header: '',
    successButtonName: '',
    icon: '',
    svg: '',
    className: '',
    modalName: '',
    handleClose: () => {},
  });

  const handleSearch = () => {
    dispatch(
      fetchPhoneTrackingNumbers({
        pagination: { page: 1, pageSize: perPage },
        search,
      })
    );
  };

  // Navigate to call or SMS history — URL drives the view
  const handleViewHistory = (phoneTrackingId, type = 'call') => {
    navigate(`/phone-tracking/${phoneTrackingId}/${type}`);
  };

  const handleHistoryActionKeyDown = (event, phoneTrackingId, type) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      handleViewHistory(phoneTrackingId, type);
    }
  };

  // Fetch history data whenever URL params change (covers direct navigation & reload)
  useEffect(() => {
    let isActive = true;

    const loadHistory = async () => {
      if (!trackingId) return;

      if (historyType === 'sms') {
        await dispatch(fetchSmsTrackingHistory({ id: trackingId }));
      } else {
        await dispatch(fetchPhoneTrackingHistory({ id: trackingId }));
      }

      if (isActive) {
        setLoadedHistoryRequestKey(historyRequestKey);
      }
    };

    loadHistory();

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingId, historyType]);

  // Handle phone number release
  const handleReleasePhoneNumber = async () => {
    if (selectedRow) {
      try {
        setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
        const response = await dispatch(releasePhoneTrackingNumber(selectedRow.id));

        if (response.status === 200) {
          // Refresh the list after successful release
          dispatch(
            fetchPhoneTrackingNumbers({
              pagination: { page: currentPage, pageSize: perPage },
              search,
            })
          );

          setOpenConfirmModal((prev) => ({ ...prev, open: false, loading: false }));
        } else {
          const linkedTemplates = response?.data?.data?.templates;
          if (linkedTemplates?.length > 0) {
            setOpenConfirmModal((prev) => ({ ...prev, open: false, loading: false }));
            setLinkedTemplatesModal({ open: true, templates: linkedTemplates });
          } else {
            dispatch(
              failure(
                response?.data?.message || 'Failed to release the phone number. Please try again.'
              )
            );
          }
        }
      } catch (error) {
        console.error('Error releasing phone number:', error);
        setOpenConfirmModal((prev) => ({ ...prev, loading: false }));
      } finally {
        setOpenConfirmModal((prev) => ({ ...prev, loading: false }));
      }
    }
  };

  const handleOpenConfirmModal = () => {
    setOpenConfirmModal({
      type: 'release-phone-number',
      open: true,
      message:
        'Once released, you will no longer own the number and future calls will not be forwarded. Historical data and analytics will remain available. ',
      description: 'This action cannot be undone.',
      handleOk: handleReleasePhoneNumber,
      loading: false,
      header: 'Release Tracking Phone Number',
      successButtonName: 'Yes',
      icon: ReleasePhoneNumberCIcon,
      svg: '',
      handleClose: () => {
        setOpenConfirmModal((prev) => ({ ...prev, open: false }));
      },
    });
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Phone Name',
      width: 200,
      flex: isWideScreen ? 1 : 0,
      compactShrinkMinWidth: 80,
      compactShrinkPriority: 0,
      sortable: false,
      renderCell: (params) => {
        return (
          <Tooltip
            title={params.row.name}
            placement="bottom"
            disableHoverListener={params.row.name.length < 18}
          >
            <Typography className="phoneTrackingCellContent">{params.row.name}</Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'phoneNumber',
      headerName: 'Tracking Number',
      width: 250,
      flex: isWideScreen ? 1 : 0,
      compactShrinkMinWidth: 105,
      compactShrinkPriority: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <Typography className="phoneTrackingCellContent">
            {formatPhoneNumber(params.row.phoneNumber)}
          </Typography>
        );
      },
    },
    {
      field: 'forwardingPhoneNumber',
      headerName: 'Call Forwarding Number',
      width: 250,
      flex: isWideScreen ? 1.2 : 0,
      compactShrinkMinWidth: 105,
      compactShrinkPriority: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <Typography className="phoneTrackingCellContent">
            {formatPhoneNumber(params.row.forwardingPhoneNumber)}
          </Typography>
        );
      },
    },
    {
      field: 'smsForwardingEmail',
      headerName: 'SMS Forwarding Email',
      width: 350,
      flex: isWideScreen ? 1.5 : 0,
      compactShrinkMinWidth: 110,
      compactShrinkPriority: 0,
      sortable: false,
      renderCell: (params) => {
        const email = params.row.smsForwardingEmail;
        return email ? (
          <Tooltip title={email} placement="bottom">
            <Typography className="phoneTrackingCellContent">{email}</Typography>
          </Tooltip>
        ) : (
          <Typography className="phoneTrackingCellContent">-</Typography>
        );
      },
    },
    {
      field: 'callCount',
      headerName: 'Calls',
      width: 100,
      flex: isWideScreen ? 0.5 : 0,
      compactShrinkMinWidth: 55,
      compactShrinkPriority: 1,
      sortable: false,
      renderCell: (params) => {
        const callCount = params.row.numberOfCalls ?? 0;
        return (
          <Typography
            className="phoneTrackingCellContent phoneTrackingCountAction"
            onClick={(event) => {
              event.stopPropagation();
              handleViewHistory(params.row.id, 'call');
            }}
            onKeyDown={(event) => handleHistoryActionKeyDown(event, params.row.id, 'call')}
            role="link"
            tabIndex={0}
            title="View call history"
          >
            {callCount}
          </Typography>
        );
      },
    },
    {
      field: 'smsCount',
      headerName: 'SMS',
      width: 100,
      flex: isWideScreen ? 0.5 : 0,
      compactShrinkMinWidth: 55,
      compactShrinkPriority: 1,
      sortable: false,
      renderCell: (params) => {
        const smsCount = params.row.numberOfSms ?? 0;
        return (
          <Typography
            className="phoneTrackingCellContent phoneTrackingCountAction"
            onClick={(event) => {
              event.stopPropagation();
              handleViewHistory(params.row.id, 'sms');
            }}
            onKeyDown={(event) => handleHistoryActionKeyDown(event, params.row.id, 'sms')}
            role="link"
            tabIndex={0}
            title="View SMS history"
          >
            {smsCount}
          </Typography>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 90,
      flex: isWideScreen ? 0.6 : 0,
      compactShrinkMinWidth: 70,
      compactShrinkPriority: 1,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={params.row.status} placement="bottom">
          <Typography className="phoneTrackingCellContent">{params.row.status}</Typography>
        </Tooltip>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Activated On',
      width: 120,
      compactWidth: 140,
      flex: isWideScreen ? 0.8 : 0,
      compactShrinkMinWidth: 100,
      compactShrinkPriority: 1,
      sortable: false,
      renderCell: (params) => (
        <Typography className="phoneTrackingCellContent">
          {dateFormat(params.row.createdAt, true)}
        </Typography>
      ),
    },
    {
      field: 'Actions',
      headerName: '',
      width: 50,
      flex: isWideScreen ? 0.3 : 0,
      sortable: false,
      renderCell: (params) => {
        return (
          <>
            <IconButton
              id={`phone-tracking-actions-${params.row.id}`}
              aria-controls={
                params.row.id === selectedRow?.id && Boolean(anchorEl)
                  ? 'phone-tracking-actions-menu'
                  : undefined
              }
              aria-haspopup="menu"
              aria-expanded={
                params.row.id === selectedRow?.id && Boolean(anchorEl) ? 'true' : undefined
              }
              aria-label={`Actions for ${params.row.name}`}
              onClick={(event) => {
                event.stopPropagation();
                setAnchorEl(event.currentTarget);
                setSelectedRow(params.row);
              }}
              sx={{
                opacity: params.row.id === selectedRow?.id && Boolean(anchorEl) ? '0.8' : '0.6',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </>
        );
      },
    },
  ];

  const rows = phoneTrackingNumbers || [];

  const numberDetailsRows =
    phoneTrackingHistory?.length > 0
      ? phoneTrackingHistory.map((call) => ({
          id: call.id,
          date: dateFormat(call.createdAt, false),
          CallFrom: formatPhoneNumber(call.callFrom),
          CallTo: formatPhoneNumber(call.callTo),
          CallDuration: formatCallDuration(call.callDuration),
          Status: call.status || '',
        }))
      : [];

  const numberDetailsColumns = [
    {
      field: 'date',
      headerName: 'Date',
      width: 250,
      flex: isMediumScreen ? 1.2 : 0,
      sortable: false,
    },
    {
      field: 'CallFrom',
      headerName: 'Call From',
      width: 200,
      flex: isMediumScreen ? 1.2 : 0,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box className="callFromWrapper">
            <Typography>{params.row.CallFrom || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'CallTo',
      headerName: 'Call To',
      width: 200,
      flex: isMediumScreen ? 1.2 : 0,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box className="callFromWrapper">
            <Typography>{params.row.CallTo || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'CallDuration',
      headerName: 'Call Duration',
      width: 150,
      flex: isMediumScreen ? 1.2 : 0,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box className="callFromWrapper">
            <Typography>{params.row.CallDuration || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'Status',
      headerName: 'Status',
      width: 150,
      flex: isMediumScreen ? 1.2 : 0,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box className="callFromWrapper">
            <Typography sx={{ textTransform: 'capitalize' }}>{params.row.Status || '-'}</Typography>
          </Box>
        );
      },
    },
  ];

  const searchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
      setSearchApplied(true);
    }
  };

  const debouncedHandleKeyDown = debounce(searchKeyDown, 300);

  const handleChangePage = (event, newPage) => {
    dispatch(fetchPhoneTrackingNumbers({ pagination: { page: newPage, pageSize: perPage } }));
  };

  const smsDetailsRows =
    phoneTrackingSmsHistory?.length > 0
      ? phoneTrackingSmsHistory.map((sms) => ({
          id: sms.id,
          date: dateFormat(sms.createdAt, false),
          SmsFrom: formatPhoneNumber(sms.smsFrom),
          SmsTo: formatPhoneNumber(sms.smsTo),
          SmsText: sms.messageText || '',
        }))
      : [];

  const smsDetailsColumns = [
    {
      field: 'date',
      headerName: 'Date',
      width: 250,
      flex: isMediumScreen ? 1.2 : 0,
      sortable: false,
    },
    {
      field: 'SmsFrom',
      headerName: 'SMS From',
      width: 200,
      flex: isMediumScreen ? 1.2 : 0,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box className="callFromWrapper">
            <Typography>{params.row.SmsFrom || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'SmsTo',
      headerName: 'SMS To',
      width: 200,
      flex: isMediumScreen ? 1.2 : 0,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box className="callFromWrapper">
            <Typography>{params.row.SmsTo || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'SmsText',
      headerName: 'SMS Text',
      width: 300,
      flex: isMediumScreen ? 2 : 0,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box className="textMessageWrapper">
            <Tooltip
              title={params.row.SmsText}
              placement="bottom"
              disableHoverListener={!params.row.SmsText || params.row.SmsText.length < 30}
            >
              <Typography>{params.row.SmsText || '-'}</Typography>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const handleHistoryChangePage = (event, newPage) => {
    if (isSmsHistory) {
      dispatch(
        fetchSmsTrackingHistory({
          id: trackingId,
          pagination: { page: newPage, pageSize: smsHistoryPerPage },
        })
      );
    } else {
      dispatch(
        fetchPhoneTrackingHistory({
          id: trackingId,
          pagination: { page: newPage, pageSize: historyPerPage },
        })
      );
    }
  };

  const handleHistoryChangePageSize = (pageSize) => {
    if (isSmsHistory) {
      dispatch(fetchSmsTrackingHistory({ id: trackingId, pagination: { page: 1, pageSize } }));
    } else {
      dispatch(fetchPhoneTrackingHistory({ id: trackingId, pagination: { page: 1, pageSize } }));
    }
  };

  // Fetch phone tracking numbers and payment methods on component mount
  useEffect(() => {
    if (!search) {
      dispatch(
        fetchPhoneTrackingNumbers({
          pagination: { page: 1, pageSize: perPage },
        })
      );
      setSearchApplied(false);
    }
    dispatch(getAllPaymentMethods());
  }, [search, perPage]);

  // Handle payment method added from PhoneTracking context
  const handlePaymentMethodAdded = () => {
    if (wasPaymentMethodModalOpen) {
      setWasPaymentMethodModalOpen(false);
      setModalOpen({ type: 'activate', open: true });
      setSelectedRow(null);
    }
  };

  const isSmsHistory = historyType === 'sms';
  const activeHistoryRows = isSmsHistory ? smsDetailsRows : numberDetailsRows;
  const activeHistoryColumns = isSmsHistory ? smsDetailsColumns : numberDetailsColumns;
  const activeHistoryReduxLoader = isSmsHistory ? smsHistoryLoader : historyLoader;
  const activeHistoryLoader =
    loadedHistoryRequestKey !== historyRequestKey || activeHistoryReduxLoader;
  const activeHistoryCount = isSmsHistory ? smsHistoryCount : historyCount;
  const activeHistoryLastPage = isSmsHistory ? smsHistoryLastPage : historyLastPage;
  const activeHistoryCurrentPage = isSmsHistory ? smsHistoryCurrentPage : historyCurrentPage;
  const activeHistoryPerPage = isSmsHistory ? smsHistoryPerPage : historyPerPage;
  const activeHistoryFrom = isSmsHistory ? smsHistoryFrom : historyFrom;
  const activeHistoryTo = isSmsHistory ? smsHistoryTo : historyTo;

  const handleOpenActivateTrackingModal = () => {
    if (!isTestMode && paymentMethods.length === 0) {
      setPaymentMethodModal(true);
      setWasPaymentMethodModalOpen(true);
      return;
    }

    setModalOpen({ type: 'activate', open: true });
    setSelectedRow(null);
  };

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#f9fafb' } }} />
      <ActivateTrackingModal
        modalOpen={modalOpen}
        isCompanyEnabled={isCompanyEnabled}
        onClose={() => {
          setModalOpen((prev) => ({
            ...prev,
            open: false,
          }));
          setSelectedRow(null);
        }}
        editingId={selectedRow?.id}
        onSuccess={() => {
          // Refresh the list after successful create/update
          dispatch(
            fetchPhoneTrackingNumbers({
              pagination: { page: currentPage, pageSize: perPage },
              search,
            })
          );
        }}
      />
      <ConfirmModal openConfirmModal={openConfirmModal} />
      <LinkedTemplatesModal
        open={linkedTemplatesModal.open}
        templates={linkedTemplatesModal.templates}
        onClose={() => setLinkedTemplatesModal({ open: false, templates: [] })}
      />
      <Stripe>
        <PaymentMethod
          open={paymentMethodModal}
          handleClose={() => {
            setPaymentMethodModal(false);
          }}
          handleExport={() => {}}
          message=""
          header=""
          onSuccessMethod={handlePaymentMethodAdded}
        />
      </Stripe>

      {+totalRecordsInDB <= 0 ? (
        <NoTrackingScreen
          addNewTracking={() => {
            handleOpenActivateTrackingModal();
          }}
        />
      ) : (
        <Box className={`phoneTrackingWrapper ${!showHistory ? 'phoneTrackingListPage' : ''}`}>
          <Box className={`phoneTrackingCard ${!showHistory ? 'phoneTrackingListCard' : ''}`}>
            <Box className={`phoneTrackingHeader ${!showHistory ? 'olcHeaderWrap' : ''}`}>
              {showHistory ? (
                <OlcPageBackButton onClick={() => navigate('/phone-tracking')}>
                  Back to Tracking Phone Numbers
                </OlcPageBackButton>
              ) : (
                <Typography variant="h6" className="phoneTrackingCardTitle pageTitle">
                  Tracking Phone Numbers
                </Typography>
              )}
              {!showHistory && (
                <Box className="pageNewBtn">
                  <OlcButton
                    variant="primary"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      handleOpenActivateTrackingModal();
                    }}
                  >
                    Add New
                  </OlcButton>
                </Box>
              )}
            </Box>
            <Box className="phoneTrackingContent">
              {!showHistory && (
                <Box className="olcTableToolbar phoneTrackingToolbar">
                  <Box
                    className="searchWrapper olcSearchWrapper"
                    sx={{
                      maxWidth: 'var(--olc-search-width) !important',
                    }}
                  >
                    <Input
                      className={`searchInput ${searchApplied ? 'searchApplied' : ''}`}
                      type="text"
                      value={search}
                      onKeyDown={debouncedHandleKeyDown}
                      onChange={(e) => setSearch(e.target.value.trimStart())}
                      placeholder="Search by name or call forwarding number"
                      title="Search by name or call forwarding number"
                      endAdornment={
                        <InputAdornment position="end" className="searchInputAdornment">
                          <IconButton
                            aria-label="toggle password visibility"
                            edge="end"
                            className="searchIcon"
                            onClick={handleSearch}
                            focusRipple={false}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'transparent',
                              },
                            }}
                          >
                            <img src={ContactSearchCIcon} alt="search" />
                          </IconButton>
                          {search?.trim() && (
                            <IconButton
                              className="cpClearInputBtn"
                              size="small"
                              onClick={() => {
                                setSearch('');
                                setSearchApplied(false);
                              }}
                              edge="end"
                              sx={{ padding: '4px' }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          )}
                        </InputAdornment>
                      }
                    />
                  </Box>
                </Box>
              )}
              {!showHistory ? (
                <>
                  <Box
                    className={`olcTableGridInternal phoneTrackingTableGrid ${
                      loading ? 'table-loading' : ''
                    } ${phoneTrackingNumbers.length === 0 ? 'table-empty' : ''}`}
                  >
                    <DataGrid
                      rows={rows}
                      columns={columns}
                      shrinkColumnsToFit
                      loading={loading}
                      disableColumnMenu
                      disableColumnFilter
                      disableColumnSelector
                      disableColumnSorting
                      rowSelection={false}
                      columnHeaderHeight={47}
                      rowHeight={61}
                      slots={{
                        noRowsOverlay: () => (
                          <Typography className="noResultsText">No Results Found</Typography>
                        ),
                        noResultsOverlay: () => (
                          <Typography className="noResultsText">No Results Found</Typography>
                        ),
                      }}
                      getRowClassName={(params) => {
                        return params.row.Status === 'Released' || params.row.status === 'Released'
                          ? 'releasedRow'
                          : '';
                      }}
                      classes={{
                        ...classes,
                        virtualScroller: `phoneTrackingVirtualScroller ${phoneTrackingNumbers.length === 0 ? 'noResults' : ''}`,
                      }}
                      sx={{
                        '& .MuiDataGrid-row:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          cursor: 'pointer',
                        },
                      }}
                    />
                  </Box>
                  {(loading || totalCount > 0) && (
                    <OlcTablePagination
                      count={lastPage || 1}
                      page={currentPage || 1}
                      pageSize={perPage}
                      pageSizes={pageSizes}
                      onPageChange={handleChangePage}
                      onPageSizeChange={(event) => {
                        dispatch(
                          fetchPhoneTrackingNumbers({
                            pagination: { page: 1, pageSize: event.target.value },
                            search,
                          })
                        );
                      }}
                      from={from}
                      to={to}
                      total={totalCount}
                      loading={loading}
                    />
                  )}
                </>
              ) : (
                <>
                  {!activeHistoryLoader && activeHistoryRows.length === 0 ? (
                    <Box>
                      <NoRecords
                        icon={
                          <img
                            src={CustomFieldIcon}
                            style={{ height: '33px', width: '33px' }}
                            alt="no-records"
                          />
                        }
                        title={isSmsHistory ? 'No SMS History' : 'No Call History'}
                        message={
                          isSmsHistory ? (
                            <>
                              <p style={{ margin: '0px' }}>No SMS history available yet.</p>
                              <p style={{ margin: '0px' }}>
                                SMS messages will appear here once they are received.
                              </p>
                            </>
                          ) : (
                            <>
                              <p style={{ margin: '0px' }}>No call history available yet.</p>
                              <p style={{ margin: '0px' }}>
                                Calls will appear here once they are received.
                              </p>
                            </>
                          )
                        }
                      />
                    </Box>
                  ) : (
                    <>
                      <Box
                        className={`olcTableGridInternal phoneTrackingTableGrid ${activeHistoryLoader ? 'table-loading' : ''} ${activeHistoryRows.length === 0 ? 'table-empty' : ''}`}
                      >
                        <DataGrid
                          rows={activeHistoryLoader ? [] : activeHistoryRows}
                          columns={activeHistoryColumns}
                          loading={activeHistoryLoader}
                          disableColumnMenu
                          disableColumnFilter
                          disableColumnSelector
                          disableColumnSorting
                          rowSelection={false}
                          columnHeaderHeight={47}
                          rowHeight={61}
                          classes={classes}
                        />
                      </Box>
                      {(activeHistoryLoader || activeHistoryCount > 0) && (
                        <OlcTablePagination
                          count={activeHistoryLastPage || 1}
                          page={activeHistoryCurrentPage || 1}
                          pageSize={activeHistoryPerPage}
                          pageSizes={pageSizes}
                          onPageChange={handleHistoryChangePage}
                          onPageSizeChange={(event) =>
                            handleHistoryChangePageSize(event.target.value)
                          }
                          from={activeHistoryFrom}
                          to={activeHistoryTo}
                          total={activeHistoryCount}
                          loading={activeHistoryLoader}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          </Box>
          <OlcActionMenu
            id="phone-tracking-actions-menu"
            anchorEl={anchorEl}
            labelledBy={selectedRow ? `phone-tracking-actions-${selectedRow.id}` : undefined}
            onClose={() => {
              setAnchorEl(null);
            }}
            actions={
              selectedRow
                ? [
                    {
                      value: 'call-history',
                      icon: HistoryCIcon,
                      label: 'View Call History',
                      onClick: () => handleViewHistory(selectedRow.id, 'call'),
                    },
                    {
                      value: 'sms-history',
                      icon: HistoryCIcon,
                      label: 'View SMS History',
                      onClick: () => handleViewHistory(selectedRow.id, 'sms'),
                    },
                    {
                      value: 'edit',
                      icon: EditPhoneCIcon,
                      label: 'Edit',
                      hidden: selectedRow.status !== 'Active',
                      onClick: () => setModalOpen({ type: 'edit', open: true }),
                    },
                    {
                      value: 'release',
                      icon: ReleasePhoneCIcon,
                      label: 'Release Phone Number',
                      hidden: selectedRow.status !== 'Active',
                      onClick: handleOpenConfirmModal,
                    },
                  ]
                : []
            }
          />
        </Box>
      )}
    </>
  );
};

export default PhoneTracking;
